import React, { useState, useEffect, useMemo, useRef } from "react";
import { supabase } from "../supabase";
import { C, mono, font } from "../config/theme";
import { fmt } from "../utils/format";
import KpiCard from "../components/KpiCard";
import MiniTable from "../components/MiniTable";
import DrillDown from "../components/DrillDown";
import StatusMessage from "../components/StatusMessage";

/* ─── Labels statuts ─── */
const STATUT_COLORS = {
  "étude":   { bg: "#EFF6FF", color: C.blue,   border: C.blue },
  "travaux": { bg: "#FFFBEB", color: C.accent, border: C.accent },
  "achevé":  { bg: C.greenBg,  color: C.green,  border: C.green },
};

/* ─── Couleurs sévérité alerte ─── */
const SEVERITE = {
  critique: { bg: C.redBg,   color: C.red,    border: C.red,    label: "Critique" },
  warning:  { bg: "#FFFBEB", color: "#92400E", border: "#F59E0B", label: "Avertissement" },
  info:     { bg: "#EFF6FF", color: "#1E40AF", border: C.blue,   label: "Info" },
};

/* ─── Labels MO ─── */
const FONCTION_LABELS = {
  compagnon:    "Compagnon",
  chef_equipe:  "Chef d'équipe",
  conducteur:   "Conducteur",
  apprenti:     "Apprenti",
  charge_aff:   "Chargé d'affaires",
};

const TYPE_CHARGE_LABELS = {
  interne:  "Interne",
  interim:  "Intérim",
  externe:  "Externe",
};

/* ═══════════════════════════════════════════════════════════════════════════════
   ChantierSelector — Sélecteur de chantier avec recherche
   ═══════════════════════════════════════════════════════════════════════════════ */
function ChantierSelector({ chantiers, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = chantiers.find((c) => c.id === value);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return chantiers;
    return chantiers.filter((c) =>
      (c.code_erp || "").toLowerCase().includes(q) ||
      (c.libelle || "").toLowerCase().includes(q) ||
      (c.client || "").toLowerCase().includes(q)
    );
  }, [chantiers, search]);

  return (
    <div ref={ref} style={{ position: "relative", flex: 1, maxWidth: 560 }}>
      <label style={{ fontSize: 10, fontWeight: 600, color: C.slate, textTransform: "uppercase" }}>Chantier</label>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          marginTop: 2, padding: "10px 14px", borderRadius: 10,
          border: `1px solid ${open ? C.accent : C.border}`,
          background: C.surface, cursor: "pointer", fontFamily: font, fontSize: 13,
          display: "flex", alignItems: "center", gap: 12, transition: "border 150ms",
        }}
      >
        {selected ? (
          <>
            <span style={{ fontFamily: mono, fontWeight: 700, color: C.navy }}>{selected.code_erp}</span>
            <span style={{ color: C.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {selected.libelle}
            </span>
            <span style={{ color: C.slate, fontSize: 12 }}>{selected.client}</span>
            <StatutBadge statut={selected.statut} />
          </>
        ) : (
          <span style={{ color: C.slateLight }}>Sélectionner un chantier…</span>
        )}
        <span style={{ fontSize: 11, color: C.slate, marginLeft: 4 }}>{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, zIndex: 100,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", maxHeight: 380, display: "flex", flexDirection: "column",
        }}>
          <div style={{ padding: "10px 12px", borderBottom: `1px solid ${C.border}` }}>
            <input
              type="text"
              autoFocus
              placeholder="Rechercher par code, libellé ou client…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${C.border}`,
                fontSize: 12, fontFamily: font, outline: "none", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 16, color: C.slate, textAlign: "center", fontSize: 12 }}>Aucun chantier</div>
            ) : filtered.map((c) => (
              <div
                key={c.id}
                onClick={() => { onChange(c.id); setOpen(false); setSearch(""); }}
                style={{
                  padding: "9px 14px", cursor: "pointer", fontSize: 12,
                  background: c.id === value ? `${C.accent}10` : "transparent",
                  borderBottom: `1px solid ${C.border}`,
                  display: "flex", alignItems: "center", gap: 10,
                }}
                onMouseEnter={(e) => { if (c.id !== value) e.currentTarget.style.background = C.bg; }}
                onMouseLeave={(e) => { if (c.id !== value) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ fontFamily: mono, fontWeight: 700, color: C.navy, minWidth: 60 }}>{c.code_erp}</span>
                <span style={{ flex: 1, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.libelle}
                </span>
                <span style={{ color: C.slate, fontSize: 11, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.client}
                </span>
                <StatutBadge statut={c.statut} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Badge statut ─── */
function StatutBadge({ statut }) {
  const v = STATUT_COLORS[statut] || { bg: C.bg, color: C.slate, border: C.border };
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
      background: v.bg, color: v.color, border: `1px solid ${v.border}40`, whiteSpace: "nowrap",
    }}>
      {statut}
    </span>
  );
}

/* ─── Petite card métrique compacte (pour la section MO) ─── */
function MiniMetric({ label, value, color = C.slate, suffix = "" }) {
  return (
    <div style={{
      background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`,
      padding: "10px 14px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: C.text, fontFamily: mono }}>
        {value}{suffix}
      </div>
    </div>
  );
}

/* ─── Titre de section ─── */
function SectionTitle({ children, color = C.navy }) {
  return (
    <div style={{
      fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
      color, margin: "24px 0 12px", paddingBottom: 8, borderBottom: `2px solid ${color}20`,
    }}>
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   PAGE — Pilotage Chantier (Dashboard conducteur de travaux)
   ═══════════════════════════════════════════════════════════════════════════════ */
export default function PagePilotageChantier() {
  /* ─── État ─── */
  const [chantierId, setChantierId] = useState("");
  const [filtres, setFiltres] = useState({ chantiers: [], statuts: [] });
  const [synthese, setSynthese] = useState(null);
  const [budget, setBudget] = useState(null);
  const [moDetail, setMoDetail] = useState(null);
  const [appro, setAppro] = useState(null);
  const [alertes, setAlertes] = useState(null);
  const [drillDown, setDrillDown] = useState(null);

  const [loadingFiltres, setLoadingFiltres] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);

  /* ─── Chargement filtres (init) ─── */
  useEffect(() => {
    async function loadFiltres() {
      setLoadingFiltres(true);
      const { data, error: err } = await supabase.rpc("bv_rpc_chantier_filtres");
      if (err) {
        console.error("Erreur filtres chantier:", err);
        setError("Impossible de charger la liste des chantiers.");
        setLoadingFiltres(false);
        return;
      }
      setFiltres(data || { chantiers: [], statuts: [] });
      const liste = data?.chantiers || [];
      if (liste.length > 0) {
        const premier = liste.find((c) => c.statut === "travaux") || liste[0];
        setChantierId(premier.id);
      }
      setLoadingFiltres(false);
    }
    loadFiltres();
  }, []);

  /* ─── Chargement données (par chantier) ─── */
  useEffect(() => {
    if (!chantierId) return;

    async function loadData() {
      setLoadingData(true);
      setDrillDown(null);
      try {
        const params = { p_chantier_id: chantierId };
        const [r1, r2, r3, r4, r5] = await Promise.all([
          supabase.rpc("bv_rpc_chantier_synthese", params),
          supabase.rpc("bv_rpc_chantier_budget_avancement", params),
          supabase.rpc("bv_rpc_chantier_mo_detail", params),
          supabase.rpc("bv_rpc_chantier_appro_stock", params),
          supabase.rpc("bv_rpc_chantier_alertes", params),
        ]);

        setSynthese(r1.data || null);
        setBudget(r2.data || null);
        setMoDetail(r3.data || null);
        setAppro(r4.data || null);
        setAlertes(r5.data || null);

        [r1, r2, r3, r4, r5].forEach((r, i) => {
          if (r.error) console.error(`Erreur RPC chantier #${i + 1}:`, r.error);
        });
      } catch (err) {
        console.error("Erreur chargement pilotage chantier:", err);
        setError("Erreur lors du chargement des données.");
      } finally {
        setLoadingData(false);
      }
    }

    loadData();
  }, [chantierId]);

  /* ─── Lignes budget (tableau budget vs réel) ─── */
  const lignesBudget = useMemo(() => {
    if (!budget) return [];
    return [
      {
        nature: "Main d'œuvre", color: "#F59E0B",
        budget: budget.budget_debourse_mo, budget_av: budget.budget_avancement_mo,
        reel: budget.reel_mo, ecart: budget.ecart_mo,
      },
      {
        nature: "Fourniture", color: "#3B82F6",
        budget: budget.budget_debourse_fourniture, budget_av: budget.budget_avancement_fourniture,
        reel: budget.reel_fourniture, ecart: budget.ecart_fourniture,
      },
      {
        nature: "Sous-traitance", color: "#8B5CF6",
        budget: budget.budget_debourse_st, budget_av: budget.budget_avancement_st,
        reel: budget.reel_st, ecart: budget.ecart_st,
      },
      {
        nature: "Matériel / Location", color: "#0EA5E9",
        budget: budget.budget_debourse_materiel, budget_av: budget.budget_avancement_materiel,
        reel: budget.reel_location,
        ecart: (budget.reel_location || 0) - (budget.budget_avancement_materiel || 0),
      },
      {
        nature: "Intérim", color: "#EC4899",
        budget: null, budget_av: null,
        reel: budget.reel_interim, ecart: null,
      },
      {
        nature: "Stock", color: "#14B8A6",
        budget: null, budget_av: null,
        reel: budget.reel_stock, ecart: null,
      },
      {
        nature: "Divers", color: "#64748B",
        budget: budget.budget_debourse_divers, budget_av: budget.budget_avancement_divers,
        reel: null, ecart: null,
      },
    ];
  }, [budget]);

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDU
     ═══════════════════════════════════════════════════════════════════════════ */
  if (loadingFiltres) {
    return <div style={{ padding: "40px 0", textAlign: "center", color: C.slate }}>Chargement…</div>;
  }

  if (error) {
    return <StatusMessage type="error" message={error} action={{ label: "Réessayer", onClick: () => window.location.reload() }} />;
  }

  if (!filtres.chantiers?.length) {
    return <StatusMessage type="empty" message="Aucun chantier disponible." detail="Les données n'ont pas encore été synchronisées depuis l'ERP." />;
  }

  return (
    <div>
      {/* ─── En-tête ─── */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>Pilotage chantier</h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: C.slate }}>
          Vue conducteur de travaux — budget, avancement, MO, appro, alertes
        </p>
      </div>

      {/* ─── Sélecteur de chantier ─── */}
      <div style={{
        display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap",
        marginBottom: 20, padding: "16px 20px",
        background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`,
      }}>
        <ChantierSelector
          chantiers={filtres.chantiers}
          value={chantierId}
          onChange={setChantierId}
        />

        {/* Infos chantier sélectionné */}
        {synthese && (
          <div style={{ display: "flex", gap: 24, alignItems: "center", fontSize: 12, color: C.slate }}>
            {synthese.date_debut && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", color: C.slateLight }}>Début</div>
                <div style={{ fontWeight: 600, color: C.text }}>
                  {new Date(synthese.date_debut).toLocaleDateString("fr-FR")}
                </div>
              </div>
            )}
            {synthese.date_fin_prevue && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", color: C.slateLight }}>Fin prévue</div>
                <div style={{ fontWeight: 600, color: C.text }}>
                  {new Date(synthese.date_fin_prevue).toLocaleDateString("fr-FR")}
                </div>
              </div>
            )}
            {synthese.ts_total != null && synthese.ts_total !== 0 && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", color: C.slateLight }}>TS</div>
                <div style={{ fontWeight: 600, color: C.text, fontFamily: mono }}>
                  {fmt(synthese.ts_total)} <span style={{ color: C.slate, fontSize: 11 }}>({Number(synthese.pct_ts || 0).toFixed(1)}%)</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {loadingData && (
        <div style={{ textAlign: "center", padding: "12px 0", color: C.slate, fontSize: 13, marginBottom: 8 }}>
          Actualisation des données…
        </div>
      )}

      {/* ═══ KPI SYNTHÈSE ═══ */}
      {synthese && (
        <>
          {/* Ligne 1 — Volumétrie financière */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 16, marginBottom: 16 }}>
            <KpiCard label="Marché total (HT)" value={synthese.marche_total || 0} color={C.navy} />
            <KpiCard label="Facturé (HT)" value={synthese.facture_total || 0} color={C.accent} />
            <KpiCard label="Avancement" value={synthese.avancement_pct || 0} color={C.green} suffix="%" />
            <KpiCard label="Reste à facturer" value={synthese.raf || 0} color="#8B5CF6" />
          </div>

          {/* Ligne 2 — Marges */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 16, marginBottom: 16 }}>
            <KpiCard label="Marge brute" value={synthese.marge_brute || 0} color={C.blue} />
            <KpiCard label="Taux marge brute" value={synthese.taux_marge_brute || 0} color={C.blue} suffix="%" />
            <KpiCard
              label="Marge nette"
              value={synthese.marge_nette || 0}
              color={(synthese.marge_nette || 0) < 0 ? C.red : C.green}
            />
            <KpiCard
              label="Taux marge nette"
              value={synthese.taux_marge_nette || 0}
              color={(synthese.taux_marge_nette || 0) < 0 ? C.red : C.green}
              suffix="%"
            />
          </div>
        </>
      )}

      {/* ═══ BUDGET À AVANCEMENT ═══ */}
      {budget && (
        <>
          <SectionTitle color={C.accent}>Budget à avancement</SectionTitle>
          <MiniTable
            title={`Budget vs réel — avancement ${Number(budget.avancement_pct || 0).toFixed(1)}%`}
            color={C.accent}
            headers={[
              {
                key: "nature", label: "Nature", bold: true,
                render: (r) => (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: r.color, display: "inline-block" }} />
                    {r.nature}
                  </span>
                ),
                total: () => "Total débourse",
              },
              {
                key: "budget", label: "Budget total", align: "right", mono: true,
                render: (r) => r.budget != null ? fmt(r.budget) : "—",
                total: () => fmt(budget.budget_total_debourse || 0),
              },
              {
                key: "budget_av", label: "Budget à avancement", align: "right", mono: true,
                render: (r) => r.budget_av != null ? fmt(r.budget_av) : "—",
                total: () => fmt(budget.budget_avancement_total || 0),
              },
              {
                key: "reel", label: "Réel", align: "right", mono: true, bold: true,
                render: (r) => r.reel != null ? fmt(r.reel) : "—",
                total: () => fmt(budget.reel_total || 0),
              },
              {
                key: "ecart", label: "Écart", align: "right", mono: true, bold: true,
                render: (r) => {
                  if (r.ecart == null) return "—";
                  const isNeg = r.ecart < 0;
                  return (
                    <span style={{ color: isNeg ? C.red : C.green }}>
                      {isNeg ? "" : "+"}{fmt(r.ecart)}
                    </span>
                  );
                },
                total: () => {
                  const e = budget.ecart_total || 0;
                  const isNeg = e < 0;
                  return (
                    <span style={{ color: isNeg ? C.red : C.green }}>
                      {isNeg ? "" : "+"}{fmt(e)}
                    </span>
                  );
                },
              },
            ]}
            rows={lignesBudget}
          />

          {/* Mini résumé budget : engagements + reste à dépenser */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 16, marginTop: 16, marginBottom: 16 }}>
            <MiniMetric label="Engagé fournisseur" value={fmt(budget.engage_fournisseur || 0)} color={C.blue} />
            <MiniMetric label="Engagé sous-traitance" value={fmt(budget.engage_st || 0)} color="#8B5CF6" />
            <MiniMetric label="Total engagé" value={fmt(budget.engage_total || 0)} color={C.navy} />
            <MiniMetric label="Reste à dépenser" value={fmt(budget.reste_a_depenser || 0)} color={C.accent} />
          </div>
        </>
      )}

      {/* ═══ MAIN D'ŒUVRE DÉTAILLÉE ═══ */}
      {moDetail && (
        <>
          <SectionTitle color={C.blue}>Main d'œuvre</SectionTitle>

          {/* Mini cartes heures */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginBottom: 12 }}>
            <MiniMetric label="Heures totales" value={Number(moDetail.heures_totales || 0).toLocaleString("fr-FR")} color={C.blue} suffix=" h" />
            <MiniMetric label="Heures travaillées" value={Number(moDetail.heures_travaillees || 0).toLocaleString("fr-FR")} color={C.green} suffix=" h" />
            <MiniMetric label="Budget heures" value={Number(moDetail.budget_heures || 0).toLocaleString("fr-FR")} color={C.navy} suffix=" h" />
            <MiniMetric label="Heures restantes" value={Number(moDetail.heures_restantes || 0).toLocaleString("fr-FR")} color={C.accent} suffix=" h" />
          </div>

          {/* Mini cartes coûts + taux */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginBottom: 16 }}>
            <MiniMetric label="Coût débourse" value={fmt(moDetail.cout_mo_debourse || 0)} color={C.accent} />
            <MiniMetric label="Coût de revient" value={fmt(moDetail.cout_mo_revient || 0)} color="#8B5CF6" />
            <MiniMetric label="Coût horaire moyen" value={`${Number(moDetail.cout_horaire_moyen || 0).toFixed(2).replace(".", ",")} €`} color={C.slate} />
            <MiniMetric
              label="Absences / intempéries"
              value={`${Number(moDetail.taux_absence || 0).toFixed(1)}% / ${Number(moDetail.taux_intemperies || 0).toFixed(1)}%`}
              color={C.red}
            />
          </div>

          {/* Tableaux par fonction + par type de charge */}
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
            <MiniTable
              title="Heures par fonction"
              color={C.blue}
              headers={[
                { key: "fonction", label: "Fonction", bold: true,
                  render: (r) => FONCTION_LABELS[r.fonction] || r.fonction,
                  total: () => "Total" },
                { key: "nb_salaries", label: "Effectif", align: "right", mono: true,
                  total: (rows) => rows.reduce((a, r) => a + (r.nb_salaries || 0), 0) },
                { key: "heures", label: "Heures", align: "right", mono: true, bold: true,
                  render: (r) => Number(r.heures || 0).toLocaleString("fr-FR"),
                  total: (rows) => Number(rows.reduce((a, r) => a + (r.heures || 0), 0)).toLocaleString("fr-FR") },
                { key: "cout", label: "Coût", align: "right", mono: true,
                  render: (r) => fmt(r.cout),
                  total: (rows) => fmt(rows.reduce((a, r) => a + (r.cout || 0), 0)) },
              ]}
              rows={moDetail.par_fonction || []}
            />

            <MiniTable
              title="Heures par type de charge"
              color="#8B5CF6"
              headers={[
                { key: "type_charge", label: "Type", bold: true,
                  render: (r) => TYPE_CHARGE_LABELS[r.type_charge] || r.type_charge,
                  total: () => "Total" },
                { key: "nb_salaries", label: "Effectif", align: "right", mono: true,
                  total: (rows) => rows.reduce((a, r) => a + (r.nb_salaries || 0), 0) },
                { key: "heures", label: "Heures", align: "right", mono: true, bold: true,
                  render: (r) => Number(r.heures || 0).toLocaleString("fr-FR"),
                  total: (rows) => Number(rows.reduce((a, r) => a + (r.heures || 0), 0)).toLocaleString("fr-FR") },
                { key: "cout", label: "Coût", align: "right", mono: true,
                  render: (r) => fmt(r.cout),
                  total: (rows) => fmt(rows.reduce((a, r) => a + (r.cout || 0), 0)) },
              ]}
              rows={moDetail.par_type_charge || []}
            />
          </div>

          {/* Détail par salarié — drill-down (filtres + export Excel) */}
          {(moDetail.par_salarie || []).length > 0 && (
            <>
              <div
                onClick={() => setDrillDown(drillDown === "salaries" ? null : "salaries")}
                style={{
                  cursor: "pointer", padding: "10px 16px", marginBottom: 8,
                  background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
                  fontSize: 12, color: C.blue, fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center",
                }}
              >
                <span>Détail par salarié ({(moDetail.par_salarie || []).length})</span>
                <span>{drillDown === "salaries" ? "▲ Fermer" : "▼ Ouvrir"}</span>
              </div>
              {drillDown === "salaries" && (
                <DrillDown
                  title="Heures et coûts par salarié"
                  color={C.blue}
                  columns={[
                    { key: "salarie", label: "Salarié", bold: true },
                    { key: "fonction", label: "Fonction",
                      render: (r) => FONCTION_LABELS[r.fonction] || r.fonction },
                    { key: "type_charge", label: "Type",
                      render: (r) => TYPE_CHARGE_LABELS[r.type_charge] || r.type_charge },
                    { key: "cout_horaire_debourse", label: "Coût horaire", align: "right", mono: true,
                      render: (r) => `${Number(r.cout_horaire_debourse || 0).toFixed(2).replace(".", ",")} €` },
                    { key: "heures_travaillees", label: "H. travaillées", align: "right", mono: true,
                      render: (r) => Number(r.heures_travaillees || 0).toLocaleString("fr-FR") },
                    { key: "heures_absence", label: "H. absence", align: "right", mono: true,
                      render: (r) => Number(r.heures_absence || 0).toLocaleString("fr-FR") },
                    { key: "heures_intemperies", label: "H. intempéries", align: "right", mono: true,
                      render: (r) => Number(r.heures_intemperies || 0).toLocaleString("fr-FR") },
                    { key: "heures_totales", label: "H. totales", align: "right", mono: true, bold: true,
                      render: (r) => Number(r.heures_totales || 0).toLocaleString("fr-FR") },
                    { key: "cout_debourse", label: "Coût débourse", align: "right", mono: true, bold: true,
                      render: (r) => fmt(r.cout_debourse) },
                    { key: "cout_revient", label: "Coût revient", align: "right", mono: true,
                      render: (r) => fmt(r.cout_revient) },
                  ]}
                  rows={moDetail.par_salarie || []}
                />
              )}
            </>
          )}
        </>
      )}

      {/* ═══ APPROVISIONNEMENT STOCK ═══ */}
      {appro && (appro.total_appro || 0) > 0 && (
        <>
          <SectionTitle color="#14B8A6">Approvisionnement stock</SectionTitle>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12, marginBottom: 12 }}>
            <MiniMetric label="Total appro" value={fmt(appro.total_appro || 0)} color="#14B8A6" />
            <MiniMetric
              label="% appro vs budget fourniture"
              value={`${Number(appro.pct_appro_vs_budget || 0).toFixed(2).replace(".", ",")}%`}
              color={C.slate}
            />
          </div>

          <MiniTable
            title="Détail par article"
            color="#14B8A6"
            headers={[
              { key: "code_article", label: "Code", bold: true, total: () => "Total" },
              { key: "designation", label: "Désignation", maxW: 280, total: () => "" },
              { key: "quantite_totale", label: "Quantité", align: "right", mono: true,
                render: (r) => Number(r.quantite_totale || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 }),
                total: () => "" },
              { key: "prix_unitaire_moyen", label: "PU moyen", align: "right", mono: true,
                render: (r) => `${Number(r.prix_unitaire_moyen || 0).toFixed(2).replace(".", ",")} €`,
                total: () => "" },
              { key: "montant_ht", label: "Montant HT", align: "right", mono: true, bold: true,
                render: (r) => fmt(r.montant_ht),
                total: (rows) => fmt(rows.reduce((a, r) => a + (r.montant_ht || 0), 0)) },
              { key: "nb_mouvements", label: "Mouvements", align: "right", mono: true,
                total: (rows) => rows.reduce((a, r) => a + (r.nb_mouvements || 0), 0) },
            ]}
            rows={appro.par_article || []}
          />
        </>
      )}

      {/* ═══ ALERTES ACTIVES ═══ */}
      {alertes && (alertes.nb_alertes || 0) > 0 && (
        <>
          <SectionTitle color={C.red}>
            Alertes actives ({alertes.nb_alertes}{alertes.nb_critiques > 0 ? ` — ${alertes.nb_critiques} critique${alertes.nb_critiques > 1 ? "s" : ""}` : ""})
          </SectionTitle>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
            {(alertes.alertes || []).map((a) => {
              const v = SEVERITE[a.severite] || SEVERITE.info;
              return (
                <div
                  key={a.id}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 12,
                    padding: "12px 16px", borderRadius: 10,
                    background: v.bg, border: `1px solid ${v.border}40`,
                  }}
                >
                  <span style={{
                    padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                    background: v.color, color: "#fff", whiteSpace: "nowrap",
                  }}>
                    {v.label}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: v.color }}>{a.message}</div>
                    <div style={{ fontSize: 11, color: C.slate, marginTop: 2 }}>
                      {a.type_alerte}
                      {a.valeur_seuil != null && a.valeur_actuelle != null && (
                        <> — actuel : <strong style={{ fontFamily: mono }}>{a.valeur_actuelle}</strong> / seuil : <strong style={{ fontFamily: mono }}>{a.valeur_seuil}</strong></>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: C.slate, fontFamily: mono, whiteSpace: "nowrap" }}>
                    {a.date_declenchement ? new Date(a.date_declenchement).toLocaleDateString("fr-FR") : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* État vide alertes (info, pas une erreur) */}
      {alertes && (alertes.nb_alertes || 0) === 0 && (
        <div style={{ marginTop: 24 }}>
          <StatusMessage
            type="info"
            message="Aucune alerte active sur ce chantier."
            detail="Les seuils configurés sont respectés."
          />
        </div>
      )}
    </div>
  );
}
